import React from 'react';
import classNames from 'classnames/bind';
import BelongEmployeeProfileView, {
  MODES,
} from 'components/BelongEmployeeProfileView/BelongEmployeeProfileView';
import ButtonBase from 'corecomponents/ButtonBase/ButtonBase';
import Carousel from 'nuka-carousel';
import PropTypes from 'prop-types';
import styles from './MultipleBelongEmployeeProfileView.module.css';
import { getFormattedEmployeeTitle } from './utils';

const cx = classNames.bind(styles);
const classNamesCollection = ['zero', 'one', 'two', 'three', 'four', 'five'];

/**
 * The `propTypes` below mark `firstName`, `lastName` and `profileImage`
 * required, but every caller builds these objects out of the generated employee
 * models, where all three are optional — so `InferProps` rejected the real value
 * at each call site. Declaring the props here is what types the component; the
 * runtime `propTypes` are left as they are, since they only warn.
 *
 * @param {{
 *   employees: {
 *     firstName?: string,
 *     jobTitle?: string,
 *     order?: number,
 *     topText?: import('react').ReactNode,
 *     [key: string]: any,
 *   }[],
 *   showDivider?: boolean,
 * }} props
 */
const MultipleBelongEmployeeProfileView = ({ employees, showDivider }) => {
  return (
    <>
      <div className={cx('desktop-wrapper')}>
        {employees.some((employee) => employee?.topText) && (
          <div
            className={cx(
              'labels-section-wrapper',
              `${classNamesCollection[employees.length]}`,
            )}
          >
            {employees.map((employee, index) => (
              <div
                className={cx(
                  'label-wrapper',
                  'label-top',
                  `${classNamesCollection[employees.length]}`,
                )}
                key={index}
              >
                <div className={cx('title-wrapper')}>{employee?.topText}</div>
              </div>
            ))}
          </div>
        )}
        <div
          className={cx(
            'employees-section-wrapper',
            `${classNamesCollection[employees.length]}`,
          )}
        >
          {employees.map((employee, index) => (
            <React.Fragment key={index}>
              <div
                className={cx(
                  'employee-wrapper',
                  `${classNamesCollection[employees.length]}`,
                  employee.order && 'z-10',
                )}
              >
                <BelongEmployeeProfileView
                  noLabel
                  showDivider={showDivider}
                  employee={employee}
                  mode={MODES.LARGE}
                />
              </div>
            </React.Fragment>
          ))}
        </div>
        <div className={cx('shelf-box-shadow')} />
        <div
          className={cx(
            'labels-section-wrapper',
            `${classNamesCollection[employees.length]}`,
          )}
        >
          {employees.map((employee, index) => (
            <div
              className={cx(
                'label-wrapper',
                `${classNamesCollection[employees.length]}`,
                employees?.length === 1 && 'max-w-none w-auto',
              )}
              key={index}
            >
              <div className={cx('title-wrapper')}>{employee?.firstName}</div>
              {employee.jobTitle && (
                <div className={cx('subtitle-wrapper')}>
                  {employees?.length > 1
                    ? getFormattedEmployeeTitle(employee?.jobTitle)
                    : employee?.jobTitle}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className={cx('mobile-wrapper')}>
        <Carousel
          disableEdgeSwiping
          slideIndex={0}
          speed={350}
          renderBottomCenterControls={(controls) => {
            const children = [];

            for (let i = 0; i < controls.slideCount; i += 1) {
              const childListComponent = (
                <li
                  key={i}
                  className={cx('position-list-item', {
                    currentSlide: controls.currentSlide === i,
                  })}
                >
                  <ButtonBase onClick={() => controls.goToSlide(i)}>
                    &#183;
                  </ButtonBase>
                </li>
              );
              children.push(childListComponent);
            }

            if (children.length > 1) {
              return (
                <ul className={cx('position-list-wrapper')}>
                  {children.map((child) => child)}
                </ul>
              );
            }
          }}
          renderCenterLeftControls={() => null}
          renderCenterRightControls={() => null}
        >
          {employees.map((employee, index) => (
            <div key={index}>
              {employee?.topText && (
                <div
                  className={cx(
                    'mobile-label-wrapper',
                    'label-top',
                    `${classNamesCollection[employees.length]}`,
                  )}
                >
                  <div className={cx('title-wrapper')}>{employee.topText}</div>
                </div>
              )}
              <div className={cx('mobile-employee-wrapper')}>
                <BelongEmployeeProfileView
                  employee={employee}
                  showDivider={showDivider}
                  mode={MODES.LARGE}
                />
              </div>
              <div
                className={cx(
                  'mobile-label-wrapper',
                  `${classNamesCollection[employees.length]}`,
                )}
                key={index}
              >
                <div className={cx('title-wrapper')}>{employee?.firstName}</div>
                <div className={cx('subtitle-wrapper')}>
                  {employee?.jobTitle}
                </div>
              </div>
            </div>
          ))}
        </Carousel>
      </div>
    </>
  );
};

MultipleBelongEmployeeProfileView.propTypes = {
  employees: PropTypes.arrayOf(
    PropTypes.shape({
      college: PropTypes.string,
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
      aboutMe: PropTypes.string,
      quote: PropTypes.string,
      specialities: PropTypes.string,
      homeTown: PropTypes.string,
      serviceArea: PropTypes.string,
      profileImage: PropTypes.string.isRequired,
      bannerImage: PropTypes.string,
      jobTitle: PropTypes.string,
      order: PropTypes.number,
    }),
  ).isRequired,
  showDivider: PropTypes.bool,
};

MultipleBelongEmployeeProfileView.defaultProps = {
  showDivider: false,
};

export default MultipleBelongEmployeeProfileView;
